import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableComponent, { TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { buildBookingServiceMaps } from "@/app/utils/bookingServiceSelection";
import {
  useBookingFeeConfigListAPI,
  IBookingFeeConfig,
  BOOKING_FEE_CONFIG_PAGE_SIZE,
} from "@/app/hooks/api_hooks/bookings/useBookingFeeConfigListAPI";
import { useDeactivateBookingFeeConfigAPI } from "@/app/hooks/api_hooks/bookings/useDeactivateBookingFeeConfigAPI";
import { useRouter } from "@/app/hooks/useRouter";
import QuickServiceFilters from "@/app/components/ui/QuickServiceFilters";

const dayLabel = (day: string) => {
  if (!day || day === "All") return "All days";
  if (day === "Weekday") return "Weekdays";
  if (day === "Weekend") return "Weekends";
  return day;
};

const clean = (value?: string | null) => (value ?? "").split("_").join(" ");

// v3 rows (lists, and days_of_week mapping each day to its advance days) -----------

const WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Consecutive runs, e.g. [1, 2, 3, 5] -> "1–3, 5"; labels come from `label`.
const runs = <T,>(values: T[], position: (value: T) => number, label: (value: T) => string) => {
  const parts: string[] = [];
  let start = 0;
  for (let i = 1; i <= values.length; i++) {
    if (i < values.length && position(values[i]) === position(values[i - 1]) + 1) continue;
    const first = label(values[start]);
    parts.push(i - 1 > start ? `${first}–${label(values[i - 1])}` : first);
    start = i;
  }
  return parts.join(", ");
};

const daysLabel = (days: string[]) => {
  const ordered = WEEK.filter((day) => days.includes(day));
  if (ordered.length === 7) return "All days";
  if (ordered.join() === WEEK.slice(0, 5).join()) return "Weekdays";
  if (ordered.join() === WEEK.slice(5).join()) return "Weekends";
  return runs(ordered, (day) => WEEK.indexOf(day), (day) => day.slice(0, 3));
};

const advanceLabel = (days: number[]) => runs([...days].sort((a, b) => a - b), (n) => n, String);

// Days that share the same advance days, in week order.
const dayGroups = (daysOfWeek: Record<string, number[]>) => {
  const groups: { days: string[]; advance: number[] }[] = [];
  for (const day of WEEK) {
    const advance = daysOfWeek[day];
    if (!advance) continue;
    const group = groups.find((g) => g.advance.join() === advance.join());
    if (group) group.days.push(day);
    else groups.push({ days: [day], advance });
  }
  return groups;
};

const Lines = ({ items }: { items: string[] }) =>
  items.length > 1 ? (
    <div className="flex flex-col gap-0.5">
      {items.map((item, i) => (
        <span key={i}>{item}</span>
      ))}
    </div>
  ) : (
    <>{items[0] || "-"}</>
  );

const ClinicsCell = ({ row }: { row: IBookingFeeConfig }) => {
  if (!row.clinic_ids) return <>{clean(row.clinic_name) || row.clinic_id}</>;
  const names = row.clinic_ids.map((id, i) => clean(row.clinic_names?.[i]) || id);
  return (
    <span title={names.join("\n")}>
      {names.slice(0, 2).join(", ")}
      {names.length > 2 && <span className="text-slate-400"> +{names.length - 2} more</span>}
    </span>
  );
};

const StatusBadge = ({ on }: { on: boolean }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
      on ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
    }`}
  >
    {on ? "On" : "Off"}
  </span>
);

export default function BookingFeeConfigListComponent() {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { execute: fetchClinics, clinicList } = useClinicListAPI();
  const { execute: fetchOrgServices, serviceTypes } = useOrgServicesByFilterAPI();
  const { execute: fetchConfigs, isLoading, configList, total } = useBookingFeeConfigListAPI();
  const { execute: deactivateConfigs, isLoading: isDeactivating } = useDeactivateBookingFeeConfigAPI();

  const [clinicFilter, setClinicFilter] = useState<string>("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>("");
  const [subServiceFilter, setSubServiceFilter] = useState<string>("");
  const [personaInput, setPersonaInput] = useState<string>("");
  const [personaFilter, setPersonaFilter] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const filters = {
    clinic_id: clinicFilter || undefined,
    sub_service_id: subServiceFilter || undefined,
    included_user_persona: personaFilter || undefined,
  };

  const serviceMaps = useMemo(() => buildBookingServiceMaps(serviceTypes ?? []), [serviceTypes]);

  useEffect(() => {
    fetchClinics();
    fetchOrgServices({ filterType: "booking_vms" });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setPersonaFilter(personaInput), 400);
    return () => clearTimeout(timer);
  }, [personaInput]);

  // Server-side, paginated. offset 0 on filter change (incl. initial load) resets the list.
  useEffect(() => {
    fetchConfigs(filters, 0);
    setSelectedIds([]);
  }, [clinicFilter, subServiceFilter, personaFilter]);

  const allSelected = configList.length > 0 && selectedIds.length === configList.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : configList.map((c) => c.configuration_id));
  };

  const toggleSelectOne = (configurationId: string) => {
    setSelectedIds((prev) =>
      prev.includes(configurationId)
        ? prev.filter((id) => id !== configurationId)
        : [...prev, configurationId],
    );
  };

  const handleMarkInactive = async () => {
    const result = await deactivateConfigs(selectedIds);
    if (result.status) {
      setSelectedIds([]);
      fetchConfigs(filters, 0);
    }
  };

  const clinicSelectItems = useMemo(
    () =>
      (Array.isArray(clinicList) ? clinicList : []).map((c: any) => ({
        id: c.id,
        title: clean(c.clinic_name),
      })),
    [clinicList],
  );

  const serviceTypeSelectItems = useMemo(
    () => serviceTypes.map((t) => ({ id: t.id, title: clean(t.name) })),
    [serviceTypes],
  );

  const subServiceSelectItems = useMemo(() => {
    const subServices = serviceTypeFilter
      ? serviceMaps.subServicesByServiceTypeIdMap[serviceTypeFilter] ?? []
      : Object.values(serviceMaps.subServiceByIdMap);
    return subServices.map((s) => ({ id: s.sub_service_id, title: clean(s.sub_service_name) }));
  }, [serviceMaps, serviceTypeFilter]);

  const handleServiceTypeChange = (serviceTypeId: string) => {
    setServiceTypeFilter(serviceTypeId);
    setSubServiceFilter("");
  };

  const handleQuickSubServiceSelect = (serviceId: string, subServiceId: string) => {
    if (subServiceFilter === subServiceId) {
      setSubServiceFilter("");
      return;
    }
    const resolvedServiceId = serviceMaps.subServiceToServiceIdMap[subServiceId] || serviceId;
    setServiceTypeFilter(serviceMaps.serviceIdToServiceTypeIdMap[resolvedServiceId] ?? "");
    setSubServiceFilter(subServiceId);
  };

  const handleClearFilters = () => {
    setClinicFilter("");
    setServiceTypeFilter("");
    setSubServiceFilter("");
    setPersonaInput("");
  };

  const columns: TableColumn<IBookingFeeConfig>[] = useMemo(
    () => [
      {
        id: "select",
        header: (
          <input
            type="checkbox"
            aria-label="Select all"
            className="w-4 h-4 cursor-pointer accent-[#193A6A]"
            checked={allSelected}
            onChange={toggleSelectAll}
          />
        ),
        width: 44,
        align: "center",
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label="Select configuration"
            className="w-4 h-4 cursor-pointer accent-[#193A6A]"
            checked={selectedIds.includes(row.configuration_id)}
            onChange={() => toggleSelectOne(row.configuration_id)}
          />
        ),
      },
      { id: "clinic", header: "Clinic", cell: ({ row }) => <ClinicsCell row={row} /> },
      {
        id: "sub_service",
        header: "Sub-Service",
        cell: ({ row }) => clean(row.subservice_name) || row.sub_service_id,
      },
      {
        id: "day",
        header: "Day",
        cell: ({ row }) =>
          row.days_of_week ? (
            <Lines items={dayGroups(row.days_of_week).map((g) => daysLabel(g.days))} />
          ) : (
            dayLabel(row.day_of_week ?? "")
          ),
      },
      { id: "fee", header: "Fee (₹)", cell: ({ row }) => `₹${row.booking_fee_amount}` },
      {
        id: "advance_days",
        header: "Advance Days",
        cell: ({ row }) =>
          row.days_of_week ? (
            <Lines items={dayGroups(row.days_of_week).map((g) => advanceLabel(g.advance))} />
          ) : (
            row.advance_days_for_booking_fee_applicability ?? "-"
          ),
      },
      { id: "vms", header: "VMS", cell: ({ row }) => <StatusBadge on={!!row.vms_on_off_status} /> },
      { id: "app", header: "App", cell: ({ row }) => <StatusBadge on={!!row.app_on_off_status} /> },
      {
        id: "included_persona",
        header: "Included Persona",
        cell: ({ row }) =>
          row.included_user_personas ? (
            <Lines items={row.included_user_personas.map(clean)} />
          ) : (
            clean(row.included_user_persona) || "-"
          ),
      },
      {
        id: "required_personas",
        header: "Required Personas",
        cell: ({ row }) => <Lines items={(row.required_user_personas ?? []).map(clean)} />,
      },
      {
        id: "excluded_personas",
        header: "Excluded Personas",
        cell: ({ row }) =>
          row.excluded_user_personas?.length ? (
            <div className="flex flex-col gap-0.5">
              {row.excluded_user_personas.map((persona, i) => (
                <span key={i}>{clean(persona)}</span>
              ))}
            </div>
          ) : (
            "-"
          ),
      },
      { id: "active", header: "Active", cell: ({ row }) => <StatusBadge on={!!row.is_active} /> },
    ],
    [allSelected, selectedIds, configList],
  );

  const hasMore = configList.length < total;

  return (
    <div className="bg-white rounded-md py-1">
      {/* Header */}
      <div className="flex items-center justify-between mt-2 mx-3">
        <div className="text-black text-base font-semibold">
          Booking Fee Configurations
          {total > 0 && (
            <span className="ml-2 text-xs font-normal text-slate-400">
              ({configList.length} of {total})
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.length > 0 && (
            <button
              type="button"
              disabled={isDeactivating}
              onClick={handleMarkInactive}
              className="px-4 py-2 border border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white rounded-md text-xs font-medium transition-colors disabled:opacity-50"
            >
              {isDeactivating ? "Marking..." : `Mark Inactive (${selectedIds.length})`}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`${getRouteKey("BOOKING_FEE_CONFIG", "url")}/create`)}
            className="px-4 py-2 bg-[#193A6A] hover:bg-[#11274c] text-white rounded-md text-xs font-medium transition-colors"
          >
            + New Config
          </button>
        </div>
      </div>

      <div className="mx-3 mt-4">
        <QuickServiceFilters
          excludeHcBath
          selectedSubService={subServiceFilter}
          setSelecteSubService={handleQuickSubServiceSelect}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mx-3 mt-4">
        <div className="w-[260px]">
          <div className="text-black text-xs font-normal mb-1">Clinic</div>
          <SearchableSelectMenu
            placeholder="All clinics"
            selectItems={clinicSelectItems}
            register={{ name: "clinic_filter" } as any}
            onSelectItem={(item) => setClinicFilter(item ? item.id : "")}
            defaultSelected={clinicSelectItems.find((c) => c.id === clinicFilter)}
            showDropdownIcon={true}
            withDropDownSearch={true}
            fieldError={undefined}
            errorMessages={[]}
          />
        </div>
        <div className="w-[260px]">
          <div className="text-black text-xs font-normal mb-1">Service Type</div>
          <SearchableSelectMenu
            placeholder="All service types"
            selectItems={serviceTypeSelectItems}
            register={{ name: "service_type_filter" } as any}
            onSelectItem={(item) => handleServiceTypeChange(item ? item.id : "")}
            defaultSelected={serviceTypeSelectItems.find((t) => t.id === serviceTypeFilter)}
            showDropdownIcon={true}
            withDropDownSearch={true}
            fieldError={undefined}
            errorMessages={[]}
          />
        </div>
        <div className="w-[260px]">
          <div className="text-black text-xs font-normal mb-1">Sub-Service</div>
          <SearchableSelectMenu
            placeholder="All sub-services"
            selectItems={subServiceSelectItems}
            register={{ name: "sub_service_filter" } as any}
            onSelectItem={(item) => setSubServiceFilter(item ? item.id : "")}
            defaultSelected={subServiceSelectItems.find((s) => s.id === subServiceFilter)}
            showDropdownIcon={true}
            withDropDownSearch={true}
            fieldError={undefined}
            errorMessages={[]}
          />
        </div>
        <div className="w-[220px]">
          <div className="text-black text-xs font-normal mb-1">Included Persona</div>
          <input
            type="search"
            value={personaInput}
            onChange={(e) => setPersonaInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="e.g. premium_guest_user"
            className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-white text-xs font-medium text-black placeholder:font-normal placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#193A6A] focus:border-[#193A6A]"
          />
        </div>
        {(clinicFilter || serviceTypeFilter || subServiceFilter || personaInput) && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="px-4 py-[9px] border rounded-md text-xs font-medium text-slate-700 hover:bg-slate-50 h-9"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="mx-3 mt-5 pb-4">
        <TableComponent<IBookingFeeConfig>
          columns={columns}
          data={configList}
          getRowId={(row) => row.configuration_id}
          loading={isLoading && configList.length === 0}
          stickyHeader={true}
          emptyState={
            <div className="px-3 py-8 text-center text-slate-400 text-xs">
              No configurations found.
            </div>
          }
        />
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center pb-10">
          <button
            type="button"
            disabled={isLoading}
            onClick={() => fetchConfigs(filters, configList.length)}
            className="px-6 py-2 border border-[#193A6A] text-[#193A6A] rounded-md text-xs font-medium hover:bg-[#193A6A] hover:text-white transition-colors disabled:opacity-50"
          >
            {isLoading ? "Loading..." : `Load more (${BOOKING_FEE_CONFIG_PAGE_SIZE})`}
          </button>
        </div>
      )}
    </div>
  );
}
